import { useEffect, useState } from "react";
import { Check, X, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, asArray, errMsg, formatDate } from "../../lib/api";
import { Stars } from "../site/Reviews";
import { Empty, Pill } from "./shared";

const TONE = { approved: "green", pending: "amber", rejected: "red" };

export const ReviewsTab = ({ onChange }) => {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("pending");
  const load = () => api.get("/admin/reviews").then((r) => setItems(asArray(r))).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const act = async (r, status) => {
    try { await api.patch(`/admin/reviews/${r.id}`, { status }); toast.success(`Review ${status}`); load(); onChange(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const remove = async (r) => {
    if (!window.confirm("Delete this review?")) return;
    try { await api.delete(`/admin/reviews/${r.id}`); toast.success("Deleted"); load(); onChange(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  const list = items.filter((r) => filter === "all" || r.status === filter);

  return (
    <div>
      <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
        <h2 className="font-display font-bold text-2xl">Client reviews</h2>
        <div className="flex gap-2">
          {["pending", "approved", "rejected", "all"].map((s) => (
            <button key={s} data-testid={`reviews-filter-${s}`} onClick={() => setFilter(s)} className={`rounded-full px-4 py-1.5 text-sm capitalize border ${filter === s ? "bg-ink text-paper border-ink" : "border-ink/20"}`}>{s}</button>
          ))}
        </div>
      </div>
      {!list.length && <Empty testid="admin-reviews-empty">No {filter === "all" ? "" : filter} reviews.</Empty>}
      <div className="space-y-3">
        {list.map((r) => (
          <div key={r.id} data-testid={`admin-review-${r.id}`} className="rounded-2xl bg-white border border-ink/10 p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="font-semibold">{r.name} <span className="font-normal text-ink/60">{[r.designation, r.company].filter(Boolean).join(", ")}</span></p>
                <div className="mt-1 flex items-center gap-3 text-xs text-ink/60"><Stars n={r.rating} className="w-3.5 h-3.5" /> {r.entity_type} · {formatDate(r.created_at)}</div>
              </div>
              <Pill tone={TONE[r.status]}>{r.status}</Pill>
            </div>
            <p className="mt-3 text-ink/80">{r.message}</p>
            <div className="mt-4 flex gap-2">
              {r.status !== "approved" && <button data-testid={`admin-review-approve-${r.id}`} onClick={() => act(r, "approved")} className="inline-flex items-center gap-1 rounded-full bg-emerald-700 text-white px-4 py-1.5 text-sm"><Check className="w-4 h-4" /> Approve</button>}
              {r.status !== "rejected" && <button data-testid={`admin-review-reject-${r.id}`} onClick={() => act(r, "rejected")} className="inline-flex items-center gap-1 rounded-full border border-ink/20 px-4 py-1.5 text-sm"><X className="w-4 h-4" /> {r.status === "approved" ? "Unpublish" : "Reject"}</button>}
              <button data-testid={`admin-review-delete-${r.id}`} onClick={() => remove(r)} className="inline-flex items-center gap-1 rounded-full text-red-700 px-3 py-1.5 text-sm hover:bg-red-50"><Trash2 className="w-4 h-4" /> Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
